import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, LayoutDashboard, Heart, Truck, Store, Phone } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { ASSETS, STORE_INFO } from "@/lib/assets";
import { NotificationBell } from "@/components/NotificationBell";
import { StoreStatusBadge } from "@/components/StoreStatusBadge";
import { SearchSuggest } from "@/components/SearchSuggest";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=Fresh Produce", label: "Fresh" },
  { to: "/about", label: "Our Story" },
];

export const BrandLogo = ({ className = "", markClass = "h-12", wordClass = "h-7 sm:h-8" }) => (
  <span className={`flex items-center gap-2 ${className}`}>
    <img src={ASSETS.logoMark} alt="" aria-hidden="true" className={`${markClass} w-auto object-contain`} />
    <img src={ASSETS.logoWordmark} alt="Bambam African Mart" className={`${wordClass} w-auto object-contain`} />
  </span>
);

export const Navbar = () => {
  const { count } = useCart();
  const { user } = useAuth();
  const { count: wishCount } = useWishlist();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, search } = useLocation();

  // NavLink ignores query strings, so "Shop" and "Fresh" would both light up on /shop?category=…
  const isLinkActive = (to) => {
    const [path, query] = to.split("?");
    if (pathname !== path) return false;
    const current = new URLSearchParams(search).get("category") || "";
    const target = query ? new URLSearchParams(query).get("category") || "" : "";
    return path === "/shop" ? current === target : true;
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="navbar" className="sticky top-0 z-50">
      {/* Announcement bar */}
      <div className="bg-forest text-sand">
        <div className="mx-auto flex max-w-[1440px] items-center justify-center gap-6 px-5 py-2 text-[12px] font-medium sm:justify-between lg:px-10">
          <div className="flex items-center gap-5">
            <span className="inline-flex items-center gap-1.5">
              <Truck className="h-3.5 w-3.5 text-ochre" /> Same-day delivery across Calgary
            </span>
            <span className="hidden items-center gap-1.5 md:inline-flex">
              <Store className="h-3.5 w-3.5 text-ochre" /> Free pickup at our Midlake store
            </span>
          </div>
          <a
            href={`tel:${STORE_INFO.phones[0].replace(/[^\d]/g, "")}`}
            className="hidden items-center gap-1.5 text-sand/80 transition-colors hover:text-ochre sm:inline-flex"
          >
            <Phone className="h-3.5 w-3.5" /> {STORE_INFO.phones[0]}
          </a>
        </div>
      </div>

      {/* Main bar */}
      <div
        className={`border-b transition-all duration-300 ${
          scrolled ? "border-border bg-white/90 shadow-[0_8px_30px_-12px_rgba(15,74,35,0.18)] backdrop-blur-xl" : "border-transparent bg-sand/95 backdrop-blur"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-2 px-3 py-2.5 sm:gap-4 sm:px-5 lg:px-10">
          <Link to="/" data-testid="logo-link" className="shrink-0 transition-transform duration-300 hover:scale-[1.02]">
            <BrandLogo
              className="gap-1.5 sm:gap-2"
              markClass={scrolled ? "h-9 sm:h-10" : "h-10 sm:h-12"}
              wordClass={scrolled ? "h-[18px] sm:h-7" : "h-5 sm:h-8"}
            />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={() =>
                  `relative whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    isLinkActive(l.to) ? "bg-forest text-white" : "text-ink/75 hover:bg-forest/5 hover:text-forest"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
            <StoreStatusBadge className="mr-1 hidden xl:inline-flex" />
            <SearchSuggest
              className="hidden md:block"
              formClassName="rounded-full border border-border bg-white px-3.5 py-2 transition-shadow focus-within:border-leaf focus-within:shadow-[0_0_0_4px_rgba(67,163,47,0.15)]"
              inputClassName="w-40 xl:w-56"
              placeholder="Search egusi, yam, suya…"
            />

            {user && user.role === "admin" && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-forest hover:bg-forest/5"
              >
                <LayoutDashboard className="h-4 w-4" /> Admin
              </Link>
            )}

            {user && user.role === "admin" && <NotificationBell />}

            <Link
              to="/wishlist"
              data-testid="nav-wishlist"
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-forest transition-colors hover:bg-forest/5 sm:h-10 sm:w-10"
              aria-label="Favourites"
            >
              <Heart className="h-5 w-5" />
              {wishCount > 0 && (
                <span
                  data-testid="wishlist-count"
                  className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-clay px-1 text-[11px] font-bold text-white"
                >
                  {wishCount}
                </span>
              )}
            </Link>

            <Link
              to="/account"
              data-testid="nav-account"
              aria-label="Account"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-forest transition-colors hover:bg-forest/5 sm:h-10 sm:w-10"
            >
              <User className="h-5 w-5" />
            </Link>

            <Link
              to="/cart"
              data-testid="nav-cart"
              aria-label="Cart"
              className="relative ml-1 inline-flex h-9 items-center gap-1.5 rounded-full bg-clay px-2.5 sm:h-10 sm:gap-2 sm:pl-3 text-sm font-semibold text-white shadow-[0_8px_20px_-8px_rgba(200,32,43,0.7)] transition-all hover:bg-[#A3161F] active:scale-95 sm:pr-4"
            >
              <ShoppingBag className="h-[18px] w-[18px]" />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[11px] font-bold text-clay"
                >
                  {count}
                </span>
              )}
            </Link>

            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-forest sm:h-10 sm:w-10 lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-border bg-white lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              <SearchSuggest
                className="mb-3"
                formClassName="rounded-full border border-border bg-sand px-4 py-2.5"
                dropdownClassName="left-0 right-0 w-full"
                inputClassName="w-full"
                placeholder="Search the mart…"
                onNavigate={() => setOpen(false)}
              />
              {links.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-3 py-3 font-display text-xl font-medium text-forest hover:bg-forest/5"
                >
                  {l.label}
                </Link>
              ))}
              {user && user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="rounded-xl px-3 py-3 text-base font-semibold text-clay">
                  Admin Dashboard
                </Link>
              )}
              <div className="mt-3 flex items-center justify-between rounded-2xl bg-sand px-4 py-3">
                <StoreStatusBadge />
                <span className="text-xs text-muted-foreground">{STORE_INFO.address}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
